(() => {
  'use strict';

  // Default collection sorting: Newest to Oldest
  // Only applies when the visitor has not selected a sorting option.
  const initialUrl = new URL(window.location.href);

  if (!initialUrl.searchParams.has('sort_by')) {
    initialUrl.searchParams.set('sort_by', 'created-descending');
    window.location.replace(initialUrl.toString());
    return;
  }

  if (window.__xoSoldOutLastGlobalInit) return;
  window.__xoSoldOutLastGlobalInit = true;

  const cache = new Map();
  const CACHE_TTL = 60000;
  let runId = 0;
  let timer = null;

  const parseHTML = (html) =>
    new DOMParser().parseFromString(html, 'text/html');

  function getMeta(root = document) {
    const content = root.querySelector('xo-filters-content');
    if (!content) return null;

    const meta = content.querySelector('.xo-stock-sort-meta');
    const container = content.querySelector(
      '.filters-content__grid, .filters-content__list'
    );

    if (!meta || !container) return null;

    return {
      content,
      container,
      sectionId: meta.dataset.sectionId || '',
      pageParam: meta.dataset.pageParam || 'page',
      currentPage: Math.max(
        1,
        parseInt(meta.dataset.currentPage || '1', 10)
      ),
      totalPages: Math.max(
        1,
        parseInt(meta.dataset.totalPages || '1', 10)
      ),
      pageSize: Math.max(
        1,
        parseInt(meta.dataset.pageSize || '1', 10)
      )
    };
  }

  function extractProducts(root) {
    const info = getMeta(root);
    if (!info) return [];

    const items = [];
    let pendingMarker = null;

    for (const child of Array.from(info.container.children)) {
      if (child.matches('template.xo-stock-sort-marker')) {
        pendingMarker = child;
        continue;
      }

      if (!pendingMarker) continue;

      const productId = pendingMarker.dataset.productId || '';
      const childProductId = child.getAttribute('xo-product-id') || '';

      if (productId && childProductId === productId) {
        const clone = child.cloneNode(true);

        clone
          .querySelectorAll('xo-animate')
          .forEach((el) => el.setAttribute('xo-disabled', ''));

        items.push({
          id: productId,
          available: pendingMarker.dataset.available === 'true',
          html: clone.outerHTML
        });

        pendingMarker = null;
      }
    }

    return items;
  }

  function buildCacheKey(meta) {
    const url = new URL(window.location.href);

    url.searchParams.delete(meta.pageParam);
    url.searchParams.delete('section_id');
    url.hash = '';

    return `${url.pathname}?${url.searchParams.toString()}|${meta.sectionId}`;
  }

  function buildPageUrl(meta, page) {
    const url = new URL(window.location.href);

    url.searchParams.set(meta.pageParam, String(page));

    if (meta.sectionId) {
      url.searchParams.set('section_id', meta.sectionId);
    }

    url.hash = '';

    return url.toString();
  }

  async function fetchPageProducts(meta, page) {
    if (page === meta.currentPage) {
      const currentItems = extractProducts(document);

      if (currentItems.length) {
        return currentItems;
      }
    }

    const response = await fetch(buildPageUrl(meta, page), {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Stock sort request failed: ${response.status}`
      );
    }

    const html = await response.text();

    return extractProducts(parseHTML(html));
  }

  async function getAllProducts(meta) {
    const key = buildCacheKey(meta);
    const now = Date.now();
    const cached = cache.get(key);

    if (cached && now - cached.time < CACHE_TTL) {
      return cached.promise;
    }

    const promise = Promise.all(
      Array.from(
        { length: meta.totalPages },
        (_, index) => fetchPageProducts(meta, index + 1)
      )
    )
      .then((pages) => {
        const seen = new Set();
        const items = [];

        pages.flat().forEach((item) => {
          if (!item.id || seen.has(item.id)) return;

          seen.add(item.id);
          items.push(item);
        });

        return items;
      })
      .catch((error) => {
        cache.delete(key);
        throw error;
      });

    cache.set(key, {
      time: now,
      promise
    });

    return promise;
  }

  function renderPage(meta, items) {
    const currentMeta = getMeta(document);

    if (!currentMeta) return;

    const available = items.filter(
      (item) => item.available
    );

    const soldOut = items.filter(
      (item) => !item.available
    );

    const ordered = available.concat(soldOut);

    const start =
      (currentMeta.currentPage - 1) *
      currentMeta.pageSize;

    const end =
      start + currentMeta.pageSize;

    const pageItems =
      ordered.slice(start, end);

    for (
      const child of
      Array.from(currentMeta.container.children)
    ) {
      if (
        child.matches('template.xo-stock-sort-marker') ||
        child.hasAttribute('xo-product-id')
      ) {
        child.remove();
      }
    }

    currentMeta.container.insertAdjacentHTML(
      'beforeend',
      pageItems
        .map((item) => item.html)
        .join('')
    );
  }

  async function run() {
    const id = ++runId;

    const meta = getMeta(document);

    if (!meta || meta.totalPages <= 1) {
      return;
    }

    try {
      const items = await getAllProducts(meta);

      if (id !== runId) return;

      renderPage(meta, items);
    } catch (error) {
      console.error(
        '[Sold out last]',
        error
      );
    }
  }

  function schedule() {
    clearTimeout(timer);

    timer = setTimeout(
      run,
      180
    );
  }

  function bindXOFilters(retries = 30) {
    if (
      window.xoFilters &&
      typeof window.xoFilters.on === 'function'
    ) {
      window.xoFilters.on(
        'done',
        schedule
      );

      return;
    }

    if (retries > 0) {
      setTimeout(
        () =>
          bindXOFilters(
            retries - 1
          ),
        200
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        bindXOFilters();
        schedule();
      },
      {
        once: true
      }
    );
  } else {
    bindXOFilters();
    schedule();
  }
})();